import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaymentTransactionsThunk } from "../redux/slices/paymentSlice";
import { useTranslation } from "react-i18next";

const LastFiveTransactions = () => {
  const dispatch = useDispatch();
  const userId = localStorage.getItem("userID");
  const { t } = useTranslation();
  const { transactions } = useSelector((state) => state.payment);

  // Fetch payment transactions for the logged-in user when component mounts
  useEffect(() => {
    if (userId) {
      dispatch(fetchPaymentTransactionsThunk({ userId }));
    }
  }, [dispatch, userId]);

  // Extract and sort the last 3 transactions in descending order based on `created_at`
  const lastFiveTransactions =
    transactions?.data
      ?.slice()
      ?.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      ?.slice(0, 3) || []; // Get only the last 3 transactions

  return (
    <div className="max-w-7xl mx-auto">
      {/* Container with border and shadow for a visually appealing table */}
      <div className="overflow-hidden shadow-lg border border-[#E2A32C] rounded-lg">
        {/* Section Title */}
        <h2 className="text-white text-lg sm:text-xl font-extrabold uppercase text-center py-4">
          {t("paymentTransactions.lastThreeTransactions")}
        </h2>

        {/* Transactions Table */}
        <table className="w-full rounded-lg">
          <thead className="bg-gradient-to-r from-[#C38B20] to-[#E5AA36] text-black uppercase font-semibold text-sm">
            <tr>
              <th className="text-left py-2 px-2">
                {t("paymentTransactions.date")}
              </th>
              <th className="text-left py-2 px-2">
                {t("paymentTransactions.amount")}
              </th>
              <th className="text-left py-2">
                {t("paymentTransactions.transactionFor")}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Render transactions if available */}
            {lastFiveTransactions.length > 0 ? (
              lastFiveTransactions.map((transaction) => (
                <tr key={transaction.id} className="bg-gray-800 bg-opacity-50">
                  {/* Format date to DD/MM/YYYY */}
                  <td className="py-2 px-2 text-gray-100 text-sm">
                    {new Date(transaction.created_at).toLocaleDateString(
                      "en-GB",
                      {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      }
                    )}
                  </td>

                  {/* Show amount with color indication for credit (green) and debit (red) */}
                  <td
                    className={`py-1 px-2 font-bold text-sm ${
                      transaction.transaction_type === "credit"
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    {transaction.transaction_type === "credit" ? "+ " : "- "}₹
                    {transaction.amount}
                  </td>

                  {/* Display transaction purpose with corresponding color label */}
                  <td className="py-[10px] text-left text-sm">
                    <span
                      className={`px-3 py-2 rounded-full text-white text-sm ${
                        transaction.transaction_type === "credit"
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    >
                      {transaction.transaction_for}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              // Show message when no transactions are available
              <tr>
                <td
                  colSpan="6"
                  className="text-center py-8 text-gray-600 text-lg"
                >
                  {t("paymentTransactions.noTransactions")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LastFiveTransactions;
